import { F, getIntlTranslation } from '@/utils/intl';
import { CheckCircleFilled, PlusOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import React from 'react';
import { useIntl } from 'umi';
import styles from './TemplateDrawer.less';

/**
 * One starting-point option (create step 2) as a selectable card: a thumbnail
 * sketching what you get, so the choice reads at a glance instead of as two
 * radio labels. Plain div with radio semantics — the value is local state, not
 * a form field, and AntD's Radio would fight the card-sized click target.
 */
const StartOptionCard: React.FC<{
  selected: boolean;
  onSelect: () => void;
  thumbnail: React.ReactNode;
  label: React.ReactNode;
  hint: React.ReactNode;
}> = ({ selected, onSelect, thumbnail, label, hint }) => (
  <div
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    className={`${styles.startCard} ${
      selected ? styles.startCardSelected : ''
    }`}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    {selected && <CheckCircleFilled className={styles.startCardCheck} />}
    <div className={styles.startCardThumb}>{thumbnail}</div>
    <div className={styles.startOptionLabel}>{label}</div>
    <div className={styles.startOptionHint}>{hint}</div>
  </div>
);

export interface CreateStartStepProps {
  base: 'scratch' | 'template';
  onBaseChange: (base: 'scratch' | 'template') => void;
  baseTemplateId: string | null;
  onBaseTemplateChange: (id: string) => void;
  /** Templates worth copying for this event — owned or public, same type. */
  baseTemplateOptions: { value: string; label: string }[];
}

/**
 * Step 2 of creating: where the content starts from.
 *
 * Same sibling rule as the settings panel — never inside the `body` Form.Item,
 * or its change events would write into `body`.
 */
const CreateStartStep: React.FC<CreateStartStepProps> = ({
  base,
  onBaseChange,
  baseTemplateId,
  onBaseTemplateChange,
  baseTemplateOptions,
}) => {
  const intl = useIntl();

  return (
    <div className={styles.createStepPanel}>
      <div className={styles.createStepIntro}>
        <F
          id="pages.emailCenter.templates.create.startIntro"
          defaultMessage="How do you want to start the content?"
        />
      </div>
      <div
        role="radiogroup"
        aria-label={getIntlTranslation(intl, {
          id: 'pages.emailCenter.templates.create.startIntro',
          defaultMessage: 'How do you want to start the content?',
        })}
        className={styles.startCards}
      >
        <StartOptionCard
          selected={base === 'scratch'}
          onSelect={() => onBaseChange('scratch')}
          thumbnail={
            /* An empty dashed page with a lone plus: "you draw this". */
            <div className={`${styles.thumbPage} ${styles.thumbPageEmpty}`}>
              <PlusOutlined />
            </div>
          }
          label={
            <F
              id="pages.emailCenter.templates.create.startScratch"
              defaultMessage="Start from scratch"
            />
          }
          hint={
            <F
              id="pages.emailCenter.templates.create.startScratchHint"
              defaultMessage="Begin with an empty canvas."
            />
          }
        />
        <StartOptionCard
          selected={base === 'template'}
          onSelect={() => onBaseChange('template')}
          thumbnail={
            /* A filled mini email: header, copy lines, a button. */
            <div className={styles.thumbPage}>
              <div className={styles.thumbHeader} />
              <div className={styles.thumbLine} />
              <div className={styles.thumbLine} />
              <div className={`${styles.thumbLine} ${styles.thumbLineShort}`} />
              <div className={styles.thumbButton} />
            </div>
          }
          label={
            <F
              id="pages.emailCenter.templates.create.startTemplate"
              defaultMessage="Start from an existing template"
            />
          }
          hint={
            <F
              id="pages.emailCenter.templates.create.startTemplateHint"
              defaultMessage="Copy the content of one of your templates or a shared master template. The copy is yours — the original stays untouched."
            />
          }
        />
      </div>
      {base === 'template' && (
        <Select
          className={styles.startTemplateSelect}
          showSearch
          optionFilterProp="label"
          value={baseTemplateId ?? undefined}
          onChange={onBaseTemplateChange}
          options={baseTemplateOptions}
          /* Narrowed to this event, so "nothing here" is a normal
             answer and needs to say why — antd's bare "No data" reads
             as a failed load. */
          notFoundContent={
            <F
              id="pages.emailCenter.templates.create.startTemplateEmpty"
              defaultMessage="No template for this email exists yet — start from scratch."
            />
          }
          placeholder={getIntlTranslation(intl, {
            id: 'pages.emailCenter.templates.create.startTemplatePlaceholder',
            defaultMessage: 'Select a template…',
          })}
        />
      )}
    </div>
  );
};

export default CreateStartStep;
